import { createContext, useContext, useEffect, useMemo, useState } from "react";
import React from "react";
import { FirebaseError, initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  collectionGroup,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  writeBatch,
  limit as _limit,
  query,
  startAfter,
  onSnapshot,
  getDocsFromServer,
  getCountFromServer,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  connectStorageEmulator,
} from "firebase/storage";
import {
  getAuth,
  connectAuthEmulator,
  onAuthStateChanged,
  onIdTokenChanged,
} from "firebase/auth";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { connectFirestoreEmulator } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import config from "./config.json";
import { emulators } from "../../../firebase.json";
import errorCodes from "./errorCodes.json";
import useUserStore from "../../store/userStore";

// Simple in-memory cache for secure file fetches
// Keyed by final URL and user identity to avoid leaking across users
const secureFileCache = new Map(); // key -> { expiresAt:number, buffer:ArrayBuffer, status:number, statusText:string, headers:Object }
const secureFileInflight = new Map(); // key -> Promise<{ buffer:ArrayBuffer, status:number, statusText:string, headers:Object }>

/**
 * @typedef {Object} FirebaseContextProps
 * @property {import('firebase/app').FirebaseApp} app - Firebase app instance
 * @property {import('firebase/firestore').Firestore} database - Firestore instance
 * @property {boolean} isAppReady - Determines if the app is ready to be used
 * @property {import("firebase/storage").FirebaseStorage} storage - Storage instance
 * @property {import("firebase/auth").Auth} auth - Auth instance
 * @property {import("firebase/functions").Functions} functions - Functions instance
 * @property {(options: {
 *   path: string,
 *   filters?: QueryConstraint[],
 *   limit?: number,
 *   startAfterDoc?: DocumentSnapshot,
 *   checkExistence?: boolean,
 *   getOnlyLength?: boolean
 * }) => Promise<any>} getData - Fetch a single doc or list
 * @property {(options: {
 *   group: string,
 *   filters?: QueryConstraint[],
 *   limit?: number,
 *   startAfterDoc?: DocumentSnapshot,
 *   getOnlyLength?: boolean
 * }) => Promise<any>} getCollectionGroupData - Fetch list from a collection group
 * @property {(options: {path: string, observerOnlyChanges: boolean, filters: QueryConstraint[], callback: (data: any) => void}) => () => void} subscribe - Real-time listener
 * @property {(options: {path: string, data: Object, merge?: boolean}) => Promise<any>} addDocument
 * @property {(options: {path: string, data: Object}) => Promise<any>} updateDocument
 * @property {(options: {path: string}) => Promise<any>} fetchSecureFile
 * @property {(path: string) => Promise<any>} deleteDocument
 * @property {(error: Object) => ({code: string, message: string})} parseError
 * @property {(options: {path: string, file: File, fileName: string, useUidInName?: boolean, getUrl?: boolean}) => Promise<{url: string, name: string}>} uploadFile
 * @property {(operations: Array<{type: 'set'|'update'|'delete', path: string, data?: Object}>) => Promise<boolean>} batchWrite
 * @property {(path: string) => CollectionReference} getCollectionRef
 * @property {(path: string) => DocumentReference} getDocRef
 * @property {(path: string) => string} getRandomUID
 * @property {() => string} getStorageUrl
 * @property {(callback: (user: import("firebase/auth").User | null) => void) => () => void} listenToAuthState
 */
const FirebaseContext = createContext(null);

/**
 * Firebase provider wrapping all services and helper methods
 */
function FirebaseProvider({
  children,
  hideEmulatorWarning = true,
  enableEmulator = true,
}) {
  const app = initializeApp(config);
  const auth = getAuth(app);
  const database = getFirestore(app);
  const storage = getStorage(app);
  const functions = getFunctions(app);
  const [currentUser, setCurrentUser] = useState(null);

  if (enableEmulator) {
    // Connect to emulators in development
    const HOST = "localhost";
    connectAuthEmulator(auth, `http://${HOST}:${emulators.auth.port}`);
    connectFunctionsEmulator(functions, HOST, emulators.functions.port);
    connectFirestoreEmulator(database, HOST, emulators.firestore.port);
    connectStorageEmulator(storage, HOST, 9199);
  }

  useEffect(() => {
    if (hideEmulatorWarning) {
      const warningEls = document.getElementsByClassName(
        "firebase-emulator-warning"
      );
      Array.from(warningEls).forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.display = "none";
        }
      });
    }
  }, [hideEmulatorWarning]);

  const setUser = useUserStore((state) => state.setUser);
  const resetUser = useUserStore((state) => state.resetUser);

  // const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        const profile = await getData({ path: `profiles/${user.uid}` });
        setCurrentUser(user);
        setUser({
          ...profile,
          uid: user.uid,
          isEmailVerified: user.emailVerified,
        });
      } else {
        console.log("Resetting the app!");
        resetUser();
      }
    });

    return unsubscribe;
  }, [auth]);

  // Helper to generate unique file names
  const generateFileName = (file, useUid = true) =>
    useUid ? `${uuidv4()}-${file.name}` : file.name;

  /**
   * Unified data fetcher: returns single doc or list based on path
   * @param {Object} options
   * @param {string} options.path - Firestore path (collection or doc)
   * @param {Array} [options.filters] - Firestore filter constraints for collections
   * @param {number} [options.limit] - Limit for collections
   * @param {import('firebase/firestore').DocumentSnapshot} [options.startAfterDoc] - Cursor for pagination
   * @param {boolean} [options.checkExistence] - For single doc: return boolean exists
   * @param {boolean} [options.getOnlyLength] - For collections: return size only
   */
  const getData = async ({
    path,
    filters = [],
    limit,
    startAfterDoc,
    checkExistence = false,
    getOnlyLength = false,
  }) => {
    const parts = path.split("/");
    const isDoc = parts.length % 2 === 0;

    // --- Document path ---
    if (isDoc) {
      const docRef = doc(database, path);
      const snap = await getDoc(docRef);

      if (checkExistence) return snap.exists();
      if (!snap.exists()) return null;

      return { id: snap.id, ...snap.data() };
    }

    // --- Collection path ---
    let q = query(collection(database, path), ...filters);
    if (startAfterDoc) q = query(q, startAfter(startAfterDoc));
    if (limit) q = query(q, _limit(limit));

    if (getOnlyLength) {
      const countSnap = await getCountFromServer(q);
      return countSnap.data().count;
    }

    const snap = await getDocs(q);
    return {
      docs: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
      last: snap.docs[snap.docs.length - 1] || null,
      size: snap.size,
    };
  };

  /**
   * Fetches data from a collection group with optional filters, limit and pagination
   * @param {Object} options
   * @param {string} options.group - Name of the collection group
   * @param {Array} [options.filters] - Firestore filter constraints
   * @param {number} [options.limit] - Limit for results
   * @param {import('firebase/firestore').DocumentSnapshot} [options.startAfterDoc] - Cursor for pagination
   * @param {boolean} [options.getOnlyLength] - Return only size
   */
  const getCollectionGroupData = async ({
    group,
    filters = [],
    limit,
    startAfterDoc,
    getOnlyLength = false,
  }) => {
    let q = query(collectionGroup(database, group), ...filters);
    if (startAfterDoc) q = query(q, startAfter(startAfterDoc));
    if (limit) q = query(q, _limit(limit));
    const snap = await getDocs(q);
    if (getOnlyLength) return snap.size;

    return {
      docs: snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        path: d.ref.path,
        subcollection: d.ref.parent.id, // "posts"
        parentId: d.ref.parent.parent?.id, // profile doc ID
        parentPath: d.ref.parent.parent?.path || null, // "profiles/{userId}"
      })),
      last: snap.docs[snap.docs.length - 1] || null,
      size: snap.size,
    };
  };

  /**
   * Unified realtime subscriber: listens to doc or collection based on path
   * @param {Object} params
   * @param {string} params.path - Firestore path (collection or doc)
   * @param {boolean} params.observerOnlyChanges - Will only fire the callback when there's an add or update operation.
   * Note, it only applies to collections.
   * @param {Array} params.filters - Firestore filter constraints for collections
   * @param {Function} params.callback - Called with updated data
   * @returns {Function} unsubscribe
   */
  const subscribe = ({ path, observerOnlyChanges, filters = [], callback }) => {
    const parts = path.split("/");
    const isDoc = parts.length % 2 === 0;

    if (isDoc) {
      return onSnapshot(doc(database, path), (snap) => {
        callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      });
    }

    if (observerOnlyChanges) {
      return onSnapshot(
        query(collection(database, path), ...filters),
        (snap) => {
          callback(
            snap.docChanges().map((change) => {
              if (
                (change.type === "added" || change.type === "modified") &&
                change.doc.exists()
              ) {
                return { id: change.doc.id, ...change.doc.data() };
              }
            })
          );
        }
      );
    }
    // Collection path
    return onSnapshot(query(collection(database, path), ...filters), (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  };

  const addDocument = async ({ path, data, merge = false }) => {
    const parts = path.split("/");
    if (parts.length % 2 === 0) {
      await setDoc(doc(database, path), data, { merge });
      return { id: parts.pop(), ...data };
    }
    const docRef = await addDoc(collection(database, path), data);
    return { id: docRef.id, ...data };
  };

  const updateDocument = async ({ path, data }) => {
    await setDoc(doc(database, path), data, { merge: true });
    return { id: path.split("/").pop(), ...data };
  };

  const deleteDocument = async (path) => {
    await deleteDoc(doc(database, path));
    return { id: path.split("/").pop(), deleted: true };
  };

  const uploadFile = async ({
    path,
    file,
    fileName,
    useUidInName = true,
    getUrl,
  }) => {
    const name = fileName || generateFileName(file, useUidInName);
    const storageReference = storageRef(storage, `${path}/${name}`);
    const res = await uploadBytes(storageReference, file, {
      cacheControl: "public, max-age=31536000, immutable",
    });
    const url = getUrl ? await getDownloadURL(res.ref) : "";
    return { url, name };
  };

  const batchWrite = async (operations) => {
    const batch = writeBatch(database);
    operations.forEach(({ type, path, data }) => {
      const reference = doc(database, path);
      if (type === "set") batch.set(reference, data);
      if (type === "update") batch.update(reference, data);
      if (type === "delete") batch.delete(reference);
    });
    await batch.commit();
    return true;
  };

  const getStorageUrl = () => {
    // Check if running inside Firebase emulator (by checking env vars set by Firebase emulators)
    const isDev =
      enableEmulator !== null
        ? enableEmulator
        : import.meta.env.FIREBASE_EMULATOR_HUB ||
          import.meta.FIRESTORE_EMULATOR_HOST ||
          import.meta.FIREBASE_AUTH_EMULATOR_HOST;

    const bucket = config.storageBucket;
    const base = isDev
      ? "http://localhost:9199"
      : "https://firebasestorage.googleapis.com";
    return `${base}/v0/b/${bucket}/o/`;
  };

  const fetchSecureFile = async ({
    path,
    ttlMs = 600000,
    cache = true,
    bust = false,
    cacheKey,
  } = {}) => {
    const storageUrl = getStorageUrl();
    if (!path || typeof path !== "string") {
      throw new Error("fetchSecureFile: 'path' must be a non-empty string");
    }
    const [base, query = ""] = path.includes("?")
      ? path.split("?", 2)
      : [path, ""];
    const finalUrl = `${storageUrl}${encodeURIComponent(base)}${query ? `?${query}` : ""}`;

    const user = auth.currentUser;
    const headers = {};
    if (user) {
      try {
        headers.Authorization = "Bearer " + (await user.getIdToken());
      } catch (e) {
        console.warn("fetchSecureFile: could not get ID token", e);
      }
    }

    // Build a cache key that includes URL and user identity
    const identity = user?.uid || "anon";
    const key = cacheKey || `${finalUrl}::${identity}`;

    // Serve from cache if allowed
    if (cache && !bust && ttlMs > 0) {
      const cached = secureFileCache.get(key);
      if (cached && cached.expiresAt > Date.now()) {
        return new Response(cached.buffer.slice(0), {
          status: cached.status,
          statusText: cached.statusText,
          headers: cached.headers,
        });
      }
    }

    // De-duplicate concurrent requests
    if (cache && !bust) {
      const inflight = secureFileInflight.get(key);
      if (inflight) {
        const result = await inflight;
        return new Response(result.buffer.slice(0), {
          status: result.status,
          statusText: result.statusText,
          headers: result.headers,
        });
      }
    }

    const fetchPromise = (async () => {
      const res = await fetch(finalUrl, { headers });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(
          `Failed to fetch file at: ${finalUrl}. Response: ${body}`
        );
      }
      const buffer = await res.arrayBuffer();
      const headersObj = {};
      res.headers.forEach((value, name) => {
        headersObj[name] = value;
      });
      // Store in cache
      if (cache && ttlMs > 0) {
        secureFileCache.set(key, {
          expiresAt: Date.now() + ttlMs,
          buffer,
          status: res.status,
          statusText: res.statusText,
          headers: headersObj,
        });
      }
      return {
        buffer,
        status: res.status,
        statusText: res.statusText,
        headers: headersObj,
      };
    })();

    if (cache && !bust) {
      secureFileInflight.set(
        key,
        fetchPromise.finally(() => {
          secureFileInflight.delete(key);
        })
      );
    }

    const result = await fetchPromise;
    return new Response(result.buffer.slice(0), {
      status: result.status,
      statusText: result.statusText,
      headers: result.headers,
    });
  };

  const parseError = (error) => {
    return { message: errorCodes[error?.code] || errorCodes["unknown"] };
  };

  function getCollectionRef(path) {
    if (database) {
      return collection(database, path);
    }
  }
  function getDocRef(path) {
    if (database) {
      const parts = path.split("/");
      const isDoc = parts.length % 2 === 0;
      if (isDoc) return doc(database, path);
      else return doc(collection(database, path));
    }
  }

  const value = useMemo(
    () => ({
      app,
      auth,
      database,
      storage,
      functions,
      getData,
      getCollectionGroupData,
      subscribe,
      addDocument,
      updateDocument,
      deleteDocument,
      uploadFile,
      batchWrite,
      parseError,
      fetchSecureFile,
      getCollectionRef,
      getDocRef,
      getStorageUrl,
      currentUser,
    }),
    [app, auth, currentUser]
  );

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
}

export default FirebaseProvider;

/**
 * Hook to use Firebase context
 * @returns {FirebaseContextProps}
 */
export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error("useFirebase must be used within a FirebaseProvider");
  }
  return context;
};
